import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, ImageIcon, Trash2, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CancelButton, LoaderButton } from "@/components/ui/custom-buttons";
import { Progress } from "@/components/ui/progress";
import { FileDropzone } from "@/components/ui/FileDropzone";
import InfoBulle from "@/components/ui/info-bulle";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  BATCH_IMPORT_EDIT_ROUTE,
  BATCH_IMPORT_IMAGE_ACCEPT,
  BATCH_IMPORT_WEIGHT_WARN_MB,
  BatchImportType,
} from "./batchImportConfig";
import {
  BatchImportItem,
  FINISHED_STATUSES,
  UseBatchImportReturn,
} from "./useBatchImport";

interface BatchImportDialogProps {
  type: BatchImportType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batch: UseBatchImportReturn;
}

export function BatchImportDialog({
  type,
  open,
  onOpenChange,
  batch,
}: BatchImportDialogProps) {
  const vt = useVisibleT();
  const navigate = useNavigate();
  const { items, running, done, run, reset } = batch;

  const [files, setFiles] = useState<File[]>([]);
  const [hasRejected, setHasRejected] = useState(false);

  const clearSelection = () => {
    setFiles([]);
    setHasRejected(false);
    reset();
  };

  // Fermeture : si un import tourne, il continue en arrière-plan (notif) ; s'il
  // est terminé, on garde le récap. On ne réinitialise que depuis l'écran de
  // sélection.
  const handleOpenChange = (next: boolean) => {
    if (!next && !running && !done) clearSelection();
    onOpenChange(next);
  };

  const addFiles = (dropped: File[]) => {
    setHasRejected(false);
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}-${f.size}`));
      const merged = [...prev];
      for (const f of dropped) {
        const key = `${f.name}-${f.size}`;
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(f);
        }
      }
      return merged;
    });
  };

  const removeFile = (file: File) =>
    setFiles((prev) => prev.filter((f) => f !== file));

  const total = items.length;
  const finished = items.filter((i) =>
    FINISHED_STATUSES.includes(i.status),
  ).length;
  const percent = total ? Math.round((finished / total) * 100) : 0;
  const inFlight = items.find(
    (i) => i.status === "creating" || i.status === "uploading",
  );
  const successItems = items.filter((i) => i.status === "success");
  const failedItems = items.filter(
    (i) => i.status === "error" || i.status === "skipped",
  );

  const errorLabel = useMemo(
    () =>
      (item: BatchImportItem): string => {
        if (item.status === "skipped")
          return vt(
            "bo.batchImport.error.skipped",
            "Ignorée (import interrompu)",
          );
        switch (item.error) {
          case "invalidFileFormat":
            return vt(
              "bo.batchImport.error.invalidFormat",
              "Format d'image non supporté",
            );
          case "UnprocessedFilesDueToConversionErrors":
            return vt(
              "bo.batchImport.error.conversion",
              "Échec de la conversion de l'image",
            );
          case "quotaExceded":
            return vt("bo.batchImport.error.quota", "Quota de stockage dépassé");
          default:
            return item.error ?? vt("bo.batchImport.error.unknown", "Erreur");
        }
      },
    [vt],
  );

  const goToFiche = (item: BatchImportItem) => {
    if (!item.ficheId) return;
    clearSelection();
    onOpenChange(false);
    navigate(`${BATCH_IMPORT_EDIT_ROUTE[type]}/${item.ficheId}`);
  };

  const renderSelection = () => (
    <div className="flex flex-col gap-4">
      <InfoBulle
        message={vt(
          "bo.batchImport.info",
          "Chaque image crée une fiche : le nom du fichier devient le titre et un n° d'enregistrement temporaire est attribué.",
        )}
      />

      <FileDropzone
        multiple
        accept={BATCH_IMPORT_IMAGE_ACCEPT}
        onDrop={addFiles}
        onDropRejected={() => setHasRejected(true)}
      >
        {(isDragActive) => (
          <div className="flex flex-col items-center gap-2 py-4 text-gray-500">
            <ImageIcon className="h-8 w-8" />
            <span className="text-sm font-medium">
              {isDragActive
                ? vt("bo.batchImport.dropActive", "Déposez les images ici")
                : vt(
                    "bo.batchImport.dropHint",
                    "Glissez-déposez vos images ou cliquez pour parcourir",
                  )}
            </span>
            <span className="text-xs">
              {vt(
                "bo.batchImport.acceptedFormats",
                "JPEG, PNG, WebP, TIFF, HEIC, AVIF, GIF",
              )}
            </span>
          </div>
        )}
      </FileDropzone>

      {hasRejected && (
        <p className="text-sm text-destructive">
          {vt(
            "bo.batchImport.rejectedFiles",
            "Certains fichiers ont été ignorés (format non supporté).",
          )}
        </p>
      )}

      {files.length > 0 && (
        <div className="flex max-h-72 flex-col gap-2 overflow-y-auto">
          {files.map((file) => {
            const sizeMb = file.size / (1024 * 1024);
            const tooHeavy = sizeMb > BATCH_IMPORT_WEIGHT_WARN_MB;
            return (
              <div
                key={`${file.name}-${file.size}`}
                className="flex items-center justify-between gap-2 rounded-xl border border-gray-200 p-3"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-medium">
                    {file.name}
                  </span>
                  <span
                    className={`text-xs ${tooHeavy ? "text-amber-600" : "text-gray-500"}`}
                  >
                    {sizeMb.toFixed(1)} Mo
                    {tooHeavy &&
                      ` · ${vt("bo.batchImport.warnHeavy", "image volumineuse")}`}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeFile(file)}
                  aria-label={vt("bo.batchImport.remove", "Retirer")}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderProgress = () => (
    <div className="flex flex-col gap-4 py-2">
      <Progress value={percent} />
      <p className="text-center text-sm text-gray-600">
        {finished}/{total}
        {inFlight ? ` · ${inFlight.title}` : ""}
      </p>
      <p className="text-center text-xs text-gray-400">
        {vt(
          "bo.batchImport.runningHint",
          "Vous pouvez fermer cette fenêtre : l'import continue en arrière-plan.",
        )}
      </p>
    </div>
  );

  const renderRecap = () => (
    <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
      <p className="text-sm text-gray-600">
        {vt("bo.batchImport.recapCreated", "Fiches créées")} :{" "}
        {successItems.length}
        {failedItems.length > 0 &&
          ` · ${vt("bo.batchImport.recapFailed", "échecs")} : ${failedItems.length}`}
      </p>

      {successItems.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16"></TableHead>
              <TableHead>{vt("bo.batchImport.colTitle", "Titre")}</TableHead>
              <TableHead>
                {vt("bo.batchImport.colRegistration", "N° d'enr.")}
              </TableHead>
              <TableHead className="w-28"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {successItems.map((item) => (
              <TableRow key={item.uid}>
                <TableCell>
                  {item.thumbnailUrl ? (
                    <img
                      src={item.thumbnailUrl}
                      alt=""
                      className="h-12 w-12 rounded-md border border-gray-200 object-cover"
                    />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded-md border border-gray-200">
                      <ImageIcon className="h-4 w-4 text-gray-400" />
                    </div>
                  )}
                </TableCell>
                <TableCell className="max-w-[240px] truncate">
                  {item.title}
                </TableCell>
                <TableCell>{item.registrationNumber}</TableCell>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() => goToFiche(item)}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {vt("bo.batchImport.goToFiche", "Fiche")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {failedItems.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-destructive">
            {vt("bo.batchImport.recapFailedTitle", "Images non importées")}
          </p>
          {failedItems.map((item) => (
            <div
              key={item.uid}
              className="flex items-center gap-2 rounded-lg border border-gray-200 p-2 text-sm"
            >
              <XCircle className="h-4 w-4 shrink-0 text-destructive" />
              <span className="min-w-0 flex-1 truncate">{item.title}</span>
              <span className="text-xs text-gray-500">{errorLabel(item)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {vt("bo.batchImport.title", "Importer des images en lot")}
          </DialogTitle>
          <DialogDescription>
            {vt(
              "bo.batchImport.subtitle",
              "Une fiche est créée par image importée.",
            )}
          </DialogDescription>
        </DialogHeader>

        {done ? renderRecap() : running ? renderProgress() : renderSelection()}

        <DialogFooter>
          {done ? (
            <Button
              onClick={() => {
                clearSelection();
                onOpenChange(false);
              }}
            >
              {vt("bo.batchImport.close", "Fermer")}
            </Button>
          ) : running ? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {vt("bo.batchImport.hide", "Masquer")}
            </Button>
          ) : (
            <>
              <CancelButton onClick={() => handleOpenChange(false)} />
              <LoaderButton
                isLoading={running}
                disabled={running || files.length === 0}
                onClick={() => run(files)}
              >
                {vt("bo.batchImport.launch", "Lancer l'import")}
                {files.length > 0 ? ` (${files.length})` : ""}
              </LoaderButton>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BatchImportDialog;
